import { Check } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import SearchInput from "common/components/SearchInput";
import Checkbox from "common/components/ui/Checkbox";
import {
  DropdownMenuCheckbox,
  DropdownMenuCount,
  DropdownMenuItem,
  DropdownMenuList,
  dropdownMenuItemVariants,
} from "common/components/ui/DropdownMenu";
import { LEADS_FACETS } from "common/constants";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAudienceFilters from "../../../../hooks/useAudienceFilters";
import FacetOptionsLoader from "../FacetOptionsLoader";

import type {
  AudienceFacetItem,
  AudienceFacets,
  AudienceFilters,
} from "../../../../types";

export default function AudienceFilterContent({
  facetKey,
  options,
  filters,
  setFilters,
  isLoadingFacets,
}: {
  facetKey: keyof AudienceFacets;
  options: AudienceFacetItem[];
  filters: AudienceFilters;
  setFilters: (filters: AudienceFilters) => void;
  isLoadingFacets: boolean;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const [inputValue, setInputValue] = useState("");
  const { toggleFilterItem, setFilterValue } = useAudienceFilters(
    filters,
    setFilters,
  );

  const filteredOptions = options.filter(
    (option: AudienceFacetItem) =>
      !inputValue ||
      option.name.toLowerCase().includes(inputValue.toLowerCase()),
  );

  let mainContent: ReactNode;

  if (isLoadingFacets) {
    mainContent = <FacetOptionsLoader itemsCount={2} />;
  } else if (filteredOptions.length) {
    mainContent = filteredOptions.map((option) => {
      const { id, name, target_count: targetCount } = option;
      const isSelected =
        facetKey !== "premium" && typeof id === "string"
          ? filters[facetKey].includes(id)
          : filters.premium === id;

      const content = (
        <>
          <span className="line-clamp-1 break-all">{name || "Unnamed"}</span>

          <RenderIf condition={facetKey !== "premium"}>
            <DropdownMenuCount>{targetCount}</DropdownMenuCount>
          </RenderIf>
          <RenderIf condition={facetKey === "premium" && isSelected}>
            <Check />
          </RenderIf>
        </>
      );
      if (isDesktop) {
        return (
          <DropdownMenuItem
            key={typeof id === "string" ? id : name}
            onSelect={() => {
              setFilterValue(facetKey, typeof id === "string" ? [id] : id);
            }}
          >
            {facetKey !== "premium" && (
              <DropdownMenuCheckbox
                isChecked={isSelected}
                onCheckedChange={() => {
                  if (typeof id === "string") {
                    toggleFilterItem(facetKey, id);
                  }
                }}
              />
            )}
            {content}
          </DropdownMenuItem>
        );
      }
      return (
        <button
          type="button"
          key={typeof id === "string" ? id : name}
          className={dropdownMenuItemVariants()}
          onClick={() => {
            if (typeof id === "string" && facetKey !== "premium") {
              toggleFilterItem(facetKey, id);
            } else if (typeof id !== "string") {
              setFilterValue("premium", id);
            }
          }}
        >
          <Checkbox checked={isSelected} />
          {content}
        </button>
      );
    });
  } else {
    mainContent = (
      <div className="flex items-center justify-center p-4 pt-2 text-black-500">
        No results found
      </div>
    );
  }

  return (
    <>
      {/* Show filter search input for non-premium filters */}
      <RenderIf condition={facetKey !== "premium"}>
        {isDesktop ? (
          <MenuSearchInput
            placeholder={`Search ${LEADS_FACETS[facetKey].label}...`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        ) : (
          <SearchInput
            placeholder={`Search ${LEADS_FACETS[facetKey].label}...`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onClear={() => setInputValue("")}
            className="mb-2"
          />
        )}
      </RenderIf>
      {isDesktop ? (
        <DropdownMenuList>{mainContent}</DropdownMenuList>
      ) : (
        <div className="flex flex-col">{mainContent}</div>
      )}
    </>
  );
}
